import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { openWithCacheBuster } from "./cacheBuster";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, AlertTriangle, Link2 } from "lucide-react";
import type {
	PageIssue,
	LinksToBrokenDetails,
	BrokenLinkTarget,
} from "@/types/technicalAudit";
import { sanitizeBrokenUrl } from "./issueBeforeAfter";

interface LinksToBrokenCardProps {
	issue: PageIssue;
}

function getStatusColor(code: number | null): string {
	if (code === null) return "bg-muted text-muted-foreground border-border";
	if (code >= 500) return "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800";
	if (code >= 400) return "bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800";
	return "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800";
}

// Read-only evidence for a links_to_broken issue: the source page plus every
// broken (4xx/5xx) internal link found on it. Mirrors BrokenExternalCard, but
// lists the full set of dead targets (one issue row aggregates them all).
export function LinksToBrokenCard({ issue }: LinksToBrokenCardProps) {
	const details = (issue.issue_details ?? {}) as LinksToBrokenDetails;
	const brokenLinks: BrokenLinkTarget[] = Array.isArray(details.broken_links)
		? details.broken_links
		: [];
	const brokenCount =
		typeof details.broken_count === "number"
			? details.broken_count
			: brokenLinks.length;
	const sourcePage = details.page_url || issue.page_url;

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-red-100 dark:bg-red-900/40">
					<AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-red-900 dark:text-red-300">Links to Broken Page</h4>
					<p className="text-sm mt-1 text-red-700 dark:text-red-400">
						This page links to {brokenCount} internal{" "}
						{brokenCount === 1 ? "page that returns" : "pages that return"} an
						error such as 404. Broken internal links send visitors to dead ends
						and waste crawl budget.
					</p>
				</div>
				<Badge
					variant="outline"
					className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800"
				>
					{brokenCount} broken
				</Badge>
			</div>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-950/40 flex items-center justify-center">
							<ExternalLink className="w-4 h-4 text-red-600 dark:text-red-400" />
						</div>
						Broken destinations
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0">
					<ul className="space-y-2">
						{brokenLinks.map((link, idx) => (
							<li
								key={`${link.target_url}:${idx}`}
								className="p-3 rounded-lg border bg-red-50 dark:bg-red-950/40/50 border-red-200 dark:border-red-800 space-y-1"
							>
								<div className="flex items-start justify-between gap-2">
									<p className="text-sm font-mono break-all text-red-700 dark:text-red-400 flex-1">
										{sanitizeBrokenUrl(link.target_url)}
									</p>
									<Badge
										variant="outline"
										className={getStatusColor(link.status_code)}
									>
										{link.status_code ?? "—"}
									</Badge>
								</div>
								{link.anchor && (
									<p className="text-xs text-muted-foreground">
										Anchor text: &ldquo;{link.anchor}&rdquo;
									</p>
								)}
							</li>
						))}
					</ul>
				</CardContent>
			</Card>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<Link2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						Source Page
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0">
					<a
						href={sourcePage}
						onClick={(e) => openWithCacheBuster(e, sourcePage)}
						target="_blank"
						rel="noopener noreferrer"
						className="group flex items-start gap-2 p-3 rounded-lg border bg-muted border-border hover:border-border hover:bg-muted transition-all"
					>
						<p className="text-sm font-mono break-all text-foreground flex-1">
							{sourcePage}
						</p>
						<ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
					</a>
				</CardContent>
			</Card>
		</div>
	);
}
